import React, { useState } from 'react';
import { Text, Textarea, Stack, Switch } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import CustomSlider from '../CustomSlider';
import styles from './MusicGenerationTab.module.css';

export interface MusicGenerationParams {
  prompt: string;
  musicLengthMs: number;
  mode: 'simple' | 'lyrics' | 'advanced';
  includeVocals: boolean;
  language?: string;
  customLyrics?: string;
  bpm?: number;
  musicalKey?: string;
  structure?: {
    intro?: number;
    verse?: number;
    chorus?: number;
    bridge?: number;
    outro?: number;
  };
}

interface MusicGenerationTabProps {
  onParamsChange: (params: MusicGenerationParams) => void;
  initialParams?: Partial<MusicGenerationParams>;
}

// Language options for vocals
const LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'Auto-Detect' },
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'german', label: 'German' },
  { value: 'japanese', label: 'Japanese' },
];

// Musical key options
const MUSICAL_KEYS = [
  'C Major', 'C Minor', 'C# Major', 'C# Minor',
  'D Major', 'D Minor', 'D# Major', 'D# Minor',
  'E Major', 'E Minor',
  'F Major', 'F Minor', 'F# Major', 'F# Minor',
  'G Major', 'G Minor', 'G# Major', 'G# Minor',
  'A Major', 'A Minor', 'A# Major', 'A# Minor',
  'B Major', 'B Minor',
];

// Genre options
const GENRE_OPTIONS = [
  { value: '', label: 'Select Genre...' },
  { value: 'pop', label: '🎵 Pop' },
  { value: 'rock', label: '🎸 Rock' },
  { value: 'electronic', label: '🎹 Electronic' },
  { value: 'hip-hop', label: '🎤 Hip Hop' },
  { value: 'jazz', label: '🎺 Jazz' },
  { value: 'classical', label: '🎻 Classical' },
  { value: 'country', label: '🤠 Country' },
  { value: 'r&b', label: '💫 R&B' },
  { value: 'indie', label: '🌟 Indie' },
  { value: 'folk', label: '🪕 Folk' },
  { value: 'metal', label: '⚡ Metal' },
  { value: 'reggae', label: '🌴 Reggae' },
  { value: 'ambient', label: '🌊 Ambient' },
  { value: 'cinematic', label: '🎬 Cinematic' },
];

// Mood options
const MOOD_OPTIONS = [
  { value: '', label: 'Select Mood...' },
  { value: 'upbeat', label: '😊 Upbeat' },
  { value: 'energetic', label: '⚡ Energetic' },
  { value: 'calm', label: '😌 Calm' },
  { value: 'melancholic', label: '😢 Melancholic' },
  { value: 'romantic', label: '💕 Romantic' },
  { value: 'dark', label: '🌑 Dark' },
  { value: 'epic', label: '🏆 Epic' },
  { value: 'dreamy', label: '☁️ Dreamy' },
  { value: 'aggressive', label: '💥 Aggressive' },
  { value: 'peaceful', label: '☮️ Peaceful' },
  { value: 'mysterious', label: '🔮 Mysterious' },
  { value: 'playful', label: '🎈 Playful' },
];

// Instrument options
const INSTRUMENT_OPTIONS = [
  { value: '', label: 'Select Instruments...' },
  { value: 'piano', label: '🎹 Piano' },
  { value: 'guitar', label: '🎸 Guitar' },
  { value: 'drums', label: '🥁 Drums' },
  { value: 'bass', label: '🎸 Bass' },
  { value: 'synthesizer', label: '🎛️ Synthesizer' },
  { value: 'strings', label: '🎻 Strings' },
  { value: 'brass', label: '🎺 Brass' },
  { value: 'saxophone', label: '🎷 Saxophone' },
  { value: 'flute', label: '🪈 Flute' },
  { value: 'violin', label: '🎻 Violin' },
  { value: 'cello', label: '🎼 Cello' },
  { value: 'acoustic-guitar', label: '🎸 Acoustic Guitar' },
  { value: 'electric-guitar', label: '⚡ Electric Guitar' },
];

const MusicGenerationTab: React.FC<MusicGenerationTabProps> = ({ 
  onParamsChange, 
  initialParams 
}) => {
  const [mode, setMode] = useState<'simple' | 'lyrics' | 'advanced'>(
    initialParams?.mode || 'lyrics' // Default to lyrics since simple is hidden
  );
  const [prompt, setPrompt] = useState(initialParams?.prompt || '');
  const [duration, setDuration] = useState(initialParams?.musicLengthMs || 30000);
  const [includeVocals, setIncludeVocals] = useState(
    initialParams?.includeVocals ?? true // Default to true for lyrics mode
  );
  const [language, setLanguage] = useState(initialParams?.language || 'english');
  const [customLyrics, setCustomLyrics] = useState(initialParams?.customLyrics || '');
  const [bpm, setBpm] = useState(initialParams?.bpm || 120);
  const [musicalKey, setMusicalKey] = useState(initialParams?.musicalKey || 'C Major');
  const [structure, setStructure] = useState(initialParams?.structure || {
    intro: 10,
    verse: 20,
    chorus: 15,
    bridge: 10,
    outro: 10,
  });

  // New state for music helpers
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);

  // Build enhanced prompt
  const buildEnhancedPrompt = () => {
    let enhancedPrompt = prompt;
    const additions: string[] = [];

    if (selectedGenre) {
      additions.push(selectedGenre);
    }
    if (selectedMood) {
      additions.push(selectedMood);
    }
    if (selectedInstruments.length > 0) {
      additions.push(`featuring ${selectedInstruments.join(', ')}`);
    }

    if (additions.length > 0) {
      enhancedPrompt = `${additions.join(', ')} ${prompt}`.trim();
    }

    return enhancedPrompt;
  };

  // Update parent component whenever params change
  React.useEffect(() => {
    const finalPrompt = buildEnhancedPrompt();
    
    onParamsChange({
      prompt: finalPrompt,
      musicLengthMs: duration,
      mode,
      includeVocals: mode === 'simple' ? false : mode === 'lyrics' ? true : includeVocals,
      language: mode === 'lyrics' || (mode === 'advanced' && includeVocals) ? language : undefined,
      customLyrics: mode === 'lyrics' && customLyrics.trim() ? customLyrics : undefined,
      bpm: mode === 'advanced' ? bpm : undefined,
      musicalKey: mode === 'advanced' ? musicalKey : undefined,
      structure: mode === 'advanced' ? structure : undefined,
    });
  }, [prompt, duration, mode, includeVocals, language, customLyrics, bpm, musicalKey, structure, selectedGenre, selectedMood, selectedInstruments]);

  // Calculate credits
  const creditsRequired = Math.ceil((duration / 60000) * 267);

  // Update structure section duration
  const updateStructureSection = (section: keyof typeof structure, value: number) => {
    setStructure(prev => ({ ...prev, [section]: value }));
  };

  // Calculate total structure time
  const totalStructureTime = Object.values(structure).reduce((sum, val) => sum + (val || 0), 0);
  const structureDurationMismatch = mode === 'advanced' && totalStructureTime !== duration / 1000;

  return (
    <Stack gap="md">
      {/* Mode Selector */}
      <div className={styles.modeSelector}>
        {/* SIMPLE MODE COMMENTED OUT - Instrumental only, not needed for now
        <button
          className={`${styles.modeButton} ${mode === 'simple' ? styles.active : ''}`}
          onClick={() => setMode('simple')}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: '6px' }} />
          Simple
        </button>
        */}
        <button
          className={`${styles.modeButton} ${mode === 'lyrics' ? styles.active : ''}`}
          onClick={() => setMode('lyrics')}
        >
          <FontAwesomeIcon icon={faMusic} style={{ marginRight: '6px' }} />
          Lyrics
        </button>
        <button
          className={`${styles.modeButton} ${mode === 'advanced' ? styles.active : ''}`}
          onClick={() => setMode('advanced')}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: '6px' }} />
          Advanced
        </button>
      </div>

      {/* Prompt Input - All Modes */}
      <div>
        <Text size="sm" fw={500} mb="xs">
          Music Description *
        </Text>
        <Textarea
          placeholder={
            mode === 'simple'
              ? "Describe the instrumental music... (e.g., 'Upbeat electronic dance track with synthesizers')"
              : mode === 'lyrics'
              ? "Describe the song style... (e.g., 'Indie pop song, uplifting and melodic')"
              : "Describe the composition... (e.g., 'Epic orchestral score with dramatic build-up')"
          }
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          minRows={3}
          maxRows={6}
          className={styles.musicInput}
        />
      </div>

      {/* Music Helpers - Genre, Mood, Instruments */}
      <div className={styles.musicHelpers}>
        <Text size="sm" fw={500} mb="sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          🎵 Enhance Your Music
        </Text>
        
        <div className={styles.helpersGrid}>
          {/* Genre Selector */}
          <div>
            <Text size="xs" c="dimmed" mb="xs">Genre</Text>
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              className={styles.selectInput}
            >
              {GENRE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Mood Selector */}
          <div>
            <Text size="xs" c="dimmed" mb="xs">Mood</Text>
            <select
              value={selectedMood}
              onChange={(e) => setSelectedMood(e.target.value)}
              className={styles.selectInput}
            >
              {MOOD_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Instruments Multi-Select */}
          <div className={styles.fullWidth}>
            <Text size="xs" c="dimmed" mb="xs">Instruments (select multiple)</Text>
            <div className={styles.instrumentTags}>
              {INSTRUMENT_OPTIONS.filter(i => i.value).map(instrument => (
                <button
                  key={instrument.value}
                  className={`${styles.instrumentTag} ${
                    selectedInstruments.includes(instrument.value) ? styles.active : ''
                  }`}
                  onClick={() => {
                    setSelectedInstruments(prev =>
                      prev.includes(instrument.value)
                        ? prev.filter(i => i !== instrument.value)
                        : [...prev, instrument.value]
                    );
                  }}
                  type="button"
                >
                  {instrument.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview Enhanced Prompt */}
        {(selectedGenre || selectedMood || selectedInstruments.length > 0) && (
          <div className={styles.promptPreview}>
            <Text size="xs" c="dimmed" mb="xs">✨ Enhanced Prompt:</Text>
            <Text size="xs" style={{ color: '#22d3ee', fontStyle: 'italic' }}>
              {buildEnhancedPrompt() || 'Your description will appear here...'}
            </Text>
          </div>
        )}
      </div>

      {/* Duration Slider - All Modes */}
      <div>
        <Text size="sm" fw={500} mb="xs">
          Song Duration: {(duration / 1000).toFixed(0)} seconds ({(duration / 60000).toFixed(2)} min)
        </Text>
        <CustomSlider
          value={duration}
          onChange={setDuration}
          min={10000}
          max={mode === 'advanced' ? 180000 : 120000}
          step={1000}
          label="Duration"
        />
      </div>

      {/* Vocals Toggle - Advanced Mode Only */}
      {mode === 'advanced' && (
        <div className={styles.vocalsToggle}>
          <div>
            <Text size="sm" fw={500}>Include Vocals</Text>
            <Text size="xs" c="dimmed">Add singing/voice to the music</Text>
          </div>
          <Switch
            checked={includeVocals}
            onChange={(e) => setIncludeVocals(e.currentTarget.checked)}
            size="lg"
            color="pink"
          />
        </div>
      )}

      {/* Simple Mode Info */}
      {mode === 'simple' && (
        <div className={styles.infoBox}>
          <Text size="xs">
            ℹ️ <strong>Simple Mode:</strong> Generates instrumental music without vocals. For music with singing, use Lyrics mode.
          </Text>
        </div>
      )}

      {/* LYRICS MODE SPECIFIC */}
      {mode === 'lyrics' && (
        <>
          {/* Language Selector */}
          <div>
            <Text size="sm" fw={500} mb="xs">
              Vocal Language *
            </Text>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={styles.selectInput}
            >
              {LANGUAGE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Lyrics */}
          <div>
            <Text size="sm" fw={500} mb="xs">
              Custom Lyrics (Optional)
            </Text>
            <Textarea
              placeholder={`[Verse 1]
Your lyrics here...

[Chorus]
More lyrics...

[Verse 2]
Continue your song...`}
              value={customLyrics}
              onChange={(e) => setCustomLyrics(e.target.value)}
              minRows={8}
              maxRows={12}
              className={styles.musicInput}
            />
            <Text size="xs" c="dimmed" mt="xs">
              {customLyrics.length} / 2000 characters
            </Text>
          </div>

          {/* Vocals Note */}
          <div className={styles.infoBox}>
            <Text size="xs">
              ℹ️ <strong>Note:</strong> Vocals are automatically included in lyrics mode
            </Text>
          </div>
        </>
      )}

      {/* ADVANCED MODE SPECIFIC */}
      {mode === 'advanced' && (
        <>
          <div className={styles.twoColumnGrid}>
            {/* BPM Slider */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                BPM (Beats Per Minute): {bpm}
              </Text>
              <CustomSlider
                value={bpm}
                onChange={setBpm}
                min={60}
                max={180}
                step={1}
                label="BPM"
              />
            </div>

            {/* Musical Key Selector */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                Musical Key
              </Text>
              <select
                value={musicalKey}
                onChange={(e) => setMusicalKey(e.target.value)}
                className={styles.selectInput}
              >
                {MUSICAL_KEYS.map(key => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Language Selector (if vocals enabled) */}
          {includeVocals && (
            <div>
              <Text size="sm" fw={500} mb="xs">
                Vocal Language
              </Text>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className={styles.selectInput}
              >
                {LANGUAGE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Song Structure */}
          <div className={styles.structureContainer}>
            <Text size="sm" fw={500} mb="xs">
              Song Structure (seconds)
            </Text>
            
            <div className={styles.structureGrid}>
              {Object.entries(structure).map(([section, value]) => (
                <div key={section} className={styles.structureItem}>
                  <Text size="xs" c="dimmed" tt="capitalize">
                    {section}
                  </Text>
                  <input
                    type="number"
                    value={value}
                    onChange={(e) => updateStructureSection(
                      section as keyof typeof structure,
                      parseInt(e.target.value) || 0
                    )}
                    min={0}
                    max={60}
                    className={styles.numberInput}
                  />
                  <Text size="xs" c="dimmed">s</Text>
                </div>
              ))}
            </div>

            <div className={styles.structureTotal}>
              <Text size="sm" fw={500}>
                Total: {totalStructureTime}s / {(duration / 1000).toFixed(0)}s
              </Text>
              {structureDurationMismatch && (
                <Text size="xs" c="red" mt="xs">
                  ⚠️ Structure total must match song duration
                </Text>
              )}
            </div>
          </div>
        </>
      )}
    </Stack>
  );
};

export default MusicGenerationTab;
